import type { Detention } from "@haulsheet/core";

/** Legacy app + blueprint default: 2 free hours, $50/hr. */
export const DETENTION_DEFAULTS = { freeHours: 2, rateCentsPerHour: 5000 } as const;

/**
 * Free time and rate for a new detention: whatever the driver used last
 * (synced, so every device agrees), else the defaults.
 */
export function detentionDefaults(
  rows: ReadonlyArray<Pick<Detention, "arrivedAt" | "freeHours" | "rateCentsPerHour" | "deletedAt">>,
): { freeHours: number; rateCentsPerHour: number } {
  let last: (typeof rows)[number] | undefined;
  for (const r of rows) {
    if (r.deletedAt != null) continue;
    if (!last || r.arrivedAt > last.arrivedAt) last = r;
  }
  if (!last) return { ...DETENTION_DEFAULTS };
  return { freeHours: last.freeHours, rateCentsPerHour: last.rateCentsPerHour };
}

/**
 * The DB requires departed_at > arrived_at (detention_departed_after_arrived).
 * A start/stop double-tap, or a phone clock that stepped backwards, would be
 * rejected by the server and dead-lettered — clamp to arrived + 1 s.
 */
export function departedAtFor(arrivedAt: string, now: Date = new Date()): string {
  const min = new Date(arrivedAt).getTime() + 1000;
  return new Date(Math.max(now.getTime(), min)).toISOString();
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatHours(hours: number): string {
  const mins = Math.round(hours * 60);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}

export function parseMoneyCents(s: string): number | null {
  const t = s.replace(/[$,\s]/g, "");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}
